import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { navSectionsFor, type NavItem } from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';

/**
 * Desktop sidebar. Hidden on mobile (use the bottom-tab nav).
 *
 * Items are grouped into labelled sections (My Work / Team / Communicate /
 * Knowledge / Customer / Analyze / Admin & Settings) so the nav reads as an
 * organized outline rather than a flat list. Role/capability-gated items and
 * whole empty sections render only for allowed users — the server still
 * enforces the real gate on every request. The Admin & Settings group is
 * collapsible (default open) to tuck away the gated tooling.
 */
export function Sidebar() {
  const { state } = useAuth();
  const groups = navSectionsFor(state.status === 'authenticated' ? state.me : null);
  const [adminOpen, setAdminOpen] = useState(true);

  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-kw-border bg-white md:flex">
      <div className="flex h-16 items-center border-b border-kw-border px-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex size-8 items-center justify-center rounded-kw-md bg-kw-cerulean font-bold text-white">
            K
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[15px] font-semibold text-kw-shark">Kaerwell</span>
            <span className="text-[11px] text-kw-muted">Staff Portal</span>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {groups.map(({ section, items }) => {
          const collapsible = section.collapsible === true;
          const open = !collapsible || adminOpen;
          return (
            <div key={section.id} className="mb-4 last:mb-0">
              {collapsible ? (
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => {
                    setAdminOpen((v) => !v);
                  }}
                  className="mb-1 flex w-full items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-wide text-kw-muted hover:text-kw-shark"
                >
                  <span>{section.label}</span>
                  <ChevronDown className={cn('size-3.5 transition-transform', open ? '' : '-rotate-90')} />
                </button>
              ) : (
                <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-kw-muted">
                  {section.label}
                </p>
              )}
              {open ? (
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <SidebarLink key={item.id} item={item} />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-kw-border p-4">
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-kw-md px-3 py-2 text-[13px] text-kw-muted hover:bg-kw-bg"
          onClick={() => {
            // Cmd+K palette dispatches a custom event — keeps Sidebar decoupled from CommandK.
            window.dispatchEvent(new CustomEvent('cmdk:open'));
          }}
        >
          <span>Search</span>
          <kbd className="rounded bg-kw-bg px-1.5 py-0.5 font-mono text-[11px] text-kw-muted">⌘K</kbd>
        </button>
      </div>
    </aside>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-kw-md px-3 py-2 text-[14px] font-medium transition-colors',
          isActive
            ? 'bg-kw-cerulean-light text-kw-cerulean-dark'
            : 'text-kw-txt hover:bg-kw-bg hover:text-kw-shark',
        )
      }
    >
      <item.icon className="size-[18px]" />
      <span>{item.label}</span>
    </NavLink>
  );
}
